import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Calendar, Users, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { destinations } from '../data/mock';

const DestinationCard = ({ destination }) => (
  <Card className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white">
    <div className="relative overflow-hidden">
      <div
        className="h-64 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{ backgroundImage: `url(${destination.heroImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      
      {/* Destination info overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center mb-2">
          <MapPin size={16} className="text-[#00DF98] mr-2" />
          <Badge variant="secondary" className="bg-white/20 backdrop-blur-sm text-white border-white/30">
            {destination.bestTime}
          </Badge>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">
          {destination.name}
        </h3>
        <p className="text-white/90 mb-4">
          {destination.shortDescription}
        </p>
      </div>
    </div>

    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Clock size={16} className="mr-1" />
          <span>{destination.duration}</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Users size={16} className="mr-1" />
          <span>{destination.difficulty}</span>
        </div>
      </div>

      {/* Highlights preview */}
      <div className="mb-4">
        <h4 className="font-semibold text-gray-900 mb-2">Top Highlights</h4>
        <div className="flex flex-wrap gap-2">
          {destination.highlights.slice(0, 3).map((highlight, idx) => (
            <Badge
              key={idx}
              variant="outline"
              className="text-xs bg-gradient-to-r from-[#6C30ED]/10 to-[#4E8EFF]/10 text-[#6C30ED] border-[#6C30ED]/20"
            >
              {highlight}
            </Badge>
          ))}
          {destination.highlights.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{destination.highlights.length - 3} more
            </Badge>
          )}
        </div>
      </div>

      <Link to={`/destinations/${destination.id}`}>
        <Button 
          variant="ghost" 
          className="w-full text-[#6C30ED] hover:bg-[#6C30ED]/10 font-semibold group/btn"
        >
          Explore {destination.name}
          <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const DestinationDetail = ({ destination }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % destination.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + destination.gallery.length) % destination.gallery.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${destination.heroImage})` }}
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-6">
              <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 mb-4">
                <MapPin size={14} className="mr-1" />
                {destination.name}
              </Badge>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {destination.name}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              {destination.longDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Plan Your Trip
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-8 py-4 rounded-full text-lg backdrop-blur-sm"
              >
                View Itineraries
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Destination Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Discover {destination.name}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {destination.longDescription}
              </p>
              
              {/* Quick Facts */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Best Time to Visit</h4>
                  <p className="text-gray-600">{destination.bestTime}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Typical Duration</h4>
                  <p className="text-gray-600">{destination.duration}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Currency</h4>
                  <p className="text-gray-600">{destination.currency}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Languages</h4>
                  <p className="text-gray-600">{destination.language}</p>
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={destination.gallery[currentImageIndex]}
                  alt={`${destination.name} gallery`}
                  className="w-full h-96 object-cover transition-opacity duration-500"
                />
                
                {/* Gallery navigation */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 text-white transition-all"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 text-white transition-all"
                >
                  <ChevronRight size={20} />
                </button>
                
                {/* Gallery dots */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {destination.gallery.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex 
                          ? 'bg-white scale-125' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights & Experiences */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Highlights */}
            <Card className="p-8 shadow-lg border-0">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Top Highlights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {destination.highlights.map((highlight, index) => (
                    <div
                      key={index}
                      className="flex items-center p-3 bg-gradient-to-r from-[#6C30ED]/5 to-[#4E8EFF]/5 rounded-lg border border-[#6C30ED]/10"
                    >
                      <Star size={16} className="text-[#6C30ED] mr-3 flex-shrink-0" />
                      <span className="text-gray-900 font-medium">{highlight}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Experiences */}
            <Card className="p-8 shadow-lg border-0">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Popular Experiences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {destination.experiences.map((experience, index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-[#6C30ED]/30 transition-colors"
                    >
                      <div className="w-2 h-2 bg-[#00DF98] rounded-full mr-4 flex-shrink-0"></div>
                      <span className="text-gray-900 font-medium">{experience}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sample Itineraries */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Sample Itineraries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get inspired by our carefully crafted itineraries designed to showcase the best of {destination.name}.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {destination.sampleItineraries.map((itinerary, index) => (
              <Card key={index} className="overflow-hidden shadow-lg border-0 hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-[#6C30ED]/10 to-[#4E8EFF]/10 border-b">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-900">
                        {itinerary.title}
                      </CardTitle>
                      <div className="flex items-center mt-2 space-x-4">
                        <Badge className="bg-[#6C30ED]/20 text-[#6C30ED]">
                          {itinerary.duration}
                        </Badge>
                        <Badge variant="outline">
                          {itinerary.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Highlights Include:</h4>
                  <ul className="space-y-2 mb-6">
                    {itinerary.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="w-1.5 h-1.5 bg-[#00DF98] rounded-full mr-3"></div>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white">
                    View Full Itinerary
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6C30ED]/5 via-white to-[#4E8EFF]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Explore {destination.name}?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let our travel experts create a personalized itinerary that brings your {destination.name} dreams to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Planning
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#6C30ED] text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white font-semibold px-8 py-4 rounded-full"
            >
              Download Brochure
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

const DestinationsPage = () => {
  const { destinationId } = useParams();

  // If we have a specific destination ID, show the detail view
  if (destinationId) {
    const destination = destinations.find(d => d.id === destinationId);
    if (!destination) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Destination not found</h1>
            <Link to="/destinations">
              <Button>Back to Destinations</Button>
            </Link>
          </div>
        </div>
      );
    }
    return <DestinationDetail destination={destination} />;
  }

  // Otherwise, show the destinations overview
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#6C30ED]/10 via-white to-[#4E8EFF]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Our
            <span className="block bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] bg-clip-text text-transparent">
              Destinations
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            Explore our handpicked destinations across Asia and the Middle East. Each destination 
            offers unique experiences, rich cultures, and unforgettable memories waiting to be discovered.
          </p>
          
          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#6C30ED] mb-2">8</div>
              <p className="text-gray-600">Destinations</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#00DF98] mb-2">50+</div>
              <p className="text-gray-600">Experiences</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#4E8EFF] mb-2">500+</div>
              <p className="text-gray-600">International Partners</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#FE4B79] mb-2">24/7</div>
              <p className="text-gray-600">Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinations.map((destination) => (
              <DestinationCard key={destination.id} destination={destination} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Can't Decide? We're Here to Help
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Our travel experts will help you choose the perfect destination based on your interests, 
            budget, and travel style. Get a free consultation today.
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
          >
            Get Expert Advice
          </Button>
        </div>
      </section>
    </div>
  );
};

export default DestinationsPage;